import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ArticleDisclaimer, useViewCatalogueLink } from '@/components/article';
import { NavigationPill } from '@/components/home';
import { PageHeader, PageShell } from '@/components/layout';
import { SectionHeader } from '@/components/products';
import { Button, RevealOnView } from '@/components/ui';
import {
  BUTTON_SLIDE,
  CONTENT_SLIDE,
  HEADER_FADE,
  REVEAL_TIMING,
  useInView,
} from '@/utils/animation';
import { AboutBlock, OurStoryRow, RoadmapTimeline } from './components';

const BEAT_MS = 200;
const INTRO_DELAY_MS = Math.round(HEADER_FADE.durationMs * 0.5);
const REST_DELAY_MS = INTRO_DELAY_MS + Math.round(REVEAL_TIMING.durationMs * 0.6);

const ABOUT_BLOCK_KEYS = ['mission', 'history', 'vision'] as const;
const ROADMAP_ENTRY_KEYS = ['1990', '2004', '2008', '2013', '2016', 'now'] as const;
const RECOGNITION_IMAGE = '/assets/optician-awards-2015.svg';

export default function OurStory() {
  const { t } = useTranslation();
  const viewCatalogueLink = useViewCatalogueLink();
  const [section1Visible, setSection1Visible] = useState(false);
  useEffect(() => {
    const timer = setTimeout(() => setSection1Visible(true), BEAT_MS);
    return () => clearTimeout(timer);
  }, []);

  const section2Ref = useRef<HTMLElement>(null);
  const section2InView = useInView(section2Ref, { threshold: 0 });
  const [section2Visible, setSection2Visible] = useState(false);
  useEffect(() => {
    if (!section2InView) return;
    const timer = setTimeout(() => setSection2Visible(true), BEAT_MS);
    return () => clearTimeout(timer);
  }, [section2InView]);

  const section3Ref = useRef<HTMLElement>(null);
  const section3InView = useInView(section3Ref, { threshold: 0 });
  const [section3Visible, setSection3Visible] = useState(false);
  useEffect(() => {
    if (!section3InView) return;
    const timer = setTimeout(() => setSection3Visible(true), BEAT_MS);
    return () => clearTimeout(timer);
  }, [section3InView]);

  const becomePartnerParagraphs = t('ourStory.becomePartner.paragraphs', {
    returnObjects: true,
  }) as string[];

  const roadmapEntries = ROADMAP_ENTRY_KEYS.map((key) => ({
    year: t(`home.roadmap.entries.${key}.year`),
    title: t(`home.roadmap.entries.${key}.title`),
    body: t(`home.roadmap.entries.${key}.body`, { returnObjects: true }) as string[],
  }));

  const table = t(`ourStory.our-group.table`, { returnObjects: true }) as
    | {
        columns: { key: string; label: string }[];
        rows: Record<string, string | string[]>[];
      }
    | undefined;

  return (
    <PageShell
      title={t('ourStory.meta.title')}
      description={t('ourStory.meta.description')}
      path="/our-story"
      hideHeader
    >
      <NavigationPill />
      <PageHeader title={t('ourStory.pageHeader')} links={viewCatalogueLink} />

      <section
        aria-labelledby="our-story-heading"
        className="bg-clearlab-oat px-6 py-30 md:px-14 lg:px-20 lg:py-50 xl:px-30"
      >
        <div className="mx-auto flex w-full max-w-content-xl flex-col gap-30">
          <SectionHeader
            id="our-story-heading"
            title={t('ourStory.header.title')}
            intro={t('ourStory.header.intro')}
            layout="fixed"
            visible={section1Visible}
          />

          <RevealOnView
            {...CONTENT_SLIDE}
            inView={section1Visible}
            delayMs={REST_DELAY_MS}
            className="flex flex-col gap-30"
          >
            <hr className="w-full border-0 border-t border-clearlab-divider-darker" />

            <OurStoryRow title={t('ourStory.aboutUsHeading')}>
              <div className="flex w-full flex-col items-start gap-20">
                {ABOUT_BLOCK_KEYS.map((key) => (
                  <AboutBlock
                    key={key}
                    block={{
                      label: t(`ourStory.blocks.${key}.label`),
                      body: t(`ourStory.blocks.${key}.body`),
                    }}
                  />
                ))}

                <div className="flex w-full flex-col items-start gap-10 md:flex-row md:items-start md:gap-4">
                  <div className="flex flex-1 flex-col items-start gap-2">
                    <p className="text-[16px] font-semibold leading-[18px] text-clearlab-midnight">
                      {t('ourStory.recognition.label')}
                    </p>
                    <p className="w-full text-[16px] font-normal leading-[44px] text-clearlab-midnight">
                      {t('ourStory.recognition.pre')}
                      <span className="font-semibold">{t('ourStory.recognition.highlight')}</span>
                      {t('ourStory.recognition.post')}
                    </p>
                  </div>
                  <img
                    src={RECOGNITION_IMAGE}
                    alt={t('ourStory.recognition.imageAlt')}
                    width={205}
                    height={202}
                    loading="lazy"
                    className="h-auto w-[205px] shrink-0"
                  />
                </div>

                <div className="flex w-full flex-col items-start gap-10">
                  <div className="flex w-full flex-col items-start gap-2">
                    <p className="text-[16px] font-semibold leading-[18px] text-clearlab-midnight">
                      {t('ourStory.partnership.label')}
                    </p>
                    <p className="w-full text-[16px] font-normal leading-[44px] text-clearlab-midnight">
                      {t('ourStory.partnership.body')}
                    </p>
                  </div>
                </div>
                <div className="flex w-full flex-col items-start gap-10">
                  <div className="flex w-full flex-col items-start gap-2">
                    <p className="text-[16px] font-semibold leading-[18px] text-clearlab-midnight">
                      {t('ourStory.our-group.label')}
                    </p>
                    {table && (
                      <div className="mt-6 w-full overflow-x-auto [container-type:inline-size]">
                        <table className="w-full border-separate border-spacing-0 text-[16px] font-normal leading-[20px] text-clearlab-midnight">
                          <thead>
                            <tr>
                              {table.columns.map((col, colIdx) => {
                                const shrinkClass =
                                  col.key === 'location'
                                    ? 'w-px whitespace-nowrap'
                                    : col.key === 'role'
                                      ? 'w-px'
                                      : '';
                                return (
                                  <th
                                    key={col.key}
                                    className={`border-b border-clearlab-divider-darker pb-2 text-left text-[12px] font-semibold text-clearlab-steel ${shrinkClass} ${colIdx > 0 ? 'pl-[clamp(40px,calc(6cqw_+_16px),56px)]' : ''}`}
                                  >
                                    {col.label}
                                  </th>
                                );
                              })}
                            </tr>
                          </thead>
                          <tbody>
                            {table.rows.map((row, idx) => (
                              <tr key={idx}>
                                {table.columns.map((col, colIdx) => {
                                  const value = row[col.key];
                                  const shrinkClass =
                                    col.key === 'location'
                                      ? 'w-px whitespace-nowrap'
                                      : col.key === 'role'
                                        ? 'w-px'
                                        : '';
                                  return (
                                    <td
                                      key={col.key}
                                      className={`align-top ${idx === 0 ? 'pb-4 pt-6' : 'py-4'} ${shrinkClass} ${colIdx > 0 ? 'pl-[clamp(40px,calc(6cqw_+_16px),56px)]' : ''}`}
                                    >
                                      {Array.isArray(value)
                                        ? value.map((part, partIdx) => (
                                            <span key={partIdx}>
                                              {part}
                                              {partIdx < value.length - 1 && <br />}
                                            </span>
                                          ))
                                        : value}
                                    </td>
                                  );
                                })}
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>

                  <RevealOnView {...BUTTON_SLIDE}>
                    <Button
                      to="/contact"
                      variant="outlined"
                      size="small"
                      tone="blue"
                      iconRight
                      className="px-4 py-2"
                    >
                      {t('ourStory.partnership.cta')}
                    </Button>
                  </RevealOnView>
                </div>
              </div>
            </OurStoryRow>
          </RevealOnView>
        </div>
      </section>

      <section
        ref={section2Ref}
        aria-labelledby="roadmap-heading"
        className="bg-clearlab-divider-light/20 px-6 py-30 md:px-14 lg:px-20 lg:py-50 xl:px-30"
      >
        <div className="mx-auto flex w-full max-w-content-xl flex-col">
          <OurStoryRow title={t('ourStory.roadmapHeading')} visible={section2Visible}>
            <RoadmapTimeline
              entries={roadmapEntries}
              visible={section2Visible}
              railDelayMs={Math.round(HEADER_FADE.durationMs * 0.5) + HEADER_FADE.pauseMs}
            />
          </OurStoryRow>
        </div>
        <span id="roadmap-heading" className="sr-only">
          {t('ourStory.roadmapHeading')}
        </span>
      </section>

      <section
        ref={section3Ref}
        aria-labelledby="partner-heading"
        className="bg-clearlab-oat px-6 py-30 md:px-14 lg:px-20 lg:py-50 xl:px-30"
      >
        <div className="mx-auto flex w-full max-w-content-xl flex-col gap-30">
          <OurStoryRow title={t('ourStory.becomePartner.title')} visible={section3Visible}>
            <RevealOnView
              {...CONTENT_SLIDE}
              inView={section3Visible}
              delayMs={INTRO_DELAY_MS}
              className="flex w-full flex-col items-start gap-10"
            >
              <div className="flex w-full flex-col">
                {becomePartnerParagraphs.map((paragraph, idx) => (
                  <p
                    key={idx}
                    className={`w-full text-[16px] font-normal leading-[44px] text-clearlab-midnight ${
                      idx < becomePartnerParagraphs.length - 1 ? 'mb-10' : ''
                    }`}
                  >
                    {paragraph}
                  </p>
                ))}
              </div>
              <RevealOnView {...BUTTON_SLIDE}>
                <Button
                  to="/contact"
                  variant="outlined"
                  size="small"
                  tone="blue"
                  iconRight
                  className="px-4 py-2"
                >
                  {t('ourStory.becomePartner.cta')}
                </Button>
              </RevealOnView>
            </RevealOnView>
          </OurStoryRow>
        </div>
        <span id="partner-heading" className="sr-only">
          {t('ourStory.becomePartner.title')}
        </span>
      </section>

      <ArticleDisclaimer text={t('ourStory.disclaimer')} />
    </PageShell>
  );
}
